import { DisplayId } from '@/features/list-views/components/display-id';
import { DoubleDeckerCell } from '@/features/list-views/components/double-decker-cell';
import { useLocale } from '@/lib/hooks/use-locale';
import type { ExplorerGetForStorageLocationItem } from '@/server/types/infer-trpc-types';
import type {
    BillingStatus,
    CustomDateRange,
    ExplorerSort,
    ExplorerViewFilters,
    ExplorerViewHandles,
    TargetUnit,
    VisibilityWindow,
} from '@/server/utils/explorer-utils';
import { api } from '@/utils/api';
import { forwardRef, useMemo } from 'react';
import { useConfigurableExplorerColumns } from '../hooks/use-configurable-explorer-columns';
import { useExplorerSorting } from '../hooks/use-explorer-sorting';
import useRowSelection from '../hooks/use-manage-explorer-filters';
import { usePreventExplorerUnnecissearyRerender } from '../hooks/use-prevent-explorer-unnecisseary-rerender';
import { ExplorerDataTable } from './explorer-data-table';

interface Props {
    hideUnselectedRows?: boolean;
    searchString?: string;
    window: VisibilityWindow;
    customDateRange: CustomDateRange | null;
    filters: ExplorerViewFilters;
    targetUnit: TargetUnit;
    billingStatus: BillingStatus;
    onFiltersChange: (filters: string[]) => void;
    sortingFilter: ExplorerSort | null;
    setSortingFilter: (sortingFilter: ExplorerSort | null) => void;
    yearPeriods: number;
}

export const ExplorerViewStorageLocations = forwardRef<ExplorerViewHandles, Props>(
    (
        {
            hideUnselectedRows = false,
            searchString,
            window,
            customDateRange,
            filters,
            billingStatus,
            onFiltersChange,
            sortingFilter,
            setSortingFilter,
            yearPeriods,
        },
        ref,
    ) => {
        const { t } = useLocale();

        const { sorting, sortChangedInInterface } = useExplorerSorting(sortingFilter, setSortingFilter);

        const { filtersWithRerenderProtection } = usePreventExplorerUnnecissearyRerender(
            filters,
            'storageLocation',
            hideUnselectedRows,
        );

        const {
            data: dataRaw,
            isLoading,
            isFetching,
            isFetchingNextPage,
            hasNextPage,
            fetchNextPage,
        } = api.platoUser.query.explorer.getForStorageLocation.getListWithFilters.useInfiniteQuery(
            {
                searchString,
                hideUnselectedRows,
                filters: filtersWithRerenderProtection,
                customDateRange,
                billingStatus,
                window,
                sorting: sortingFilter,
                yearPeriods,
            },
            {
                getNextPageParam: (lastPage) => lastPage.nextCursor,
            },
        );

        const columns = useConfigurableExplorerColumns({
            columns: [
                {
                    id: 'name',
                    accessorKey: 'name',
                    headerText: t('header_tab__STORAGE_LOCATION', {
                        ns: 'explorer',
                    }),
                    generateCell: (row) => (
                        <DoubleDeckerCell
                            align="left"
                            topLevel={<DisplayId id={row.id} />}
                            bottomLevel={row.name}
                        />
                    ),
                    size: 200,
                },
            ],
        });

        const data = useMemo(() => {
            return dataRaw ? dataRaw.pages.flatMap((page) => page.result) : [];
        }, [dataRaw]);

        const { rowSelection, setRowSelection } = useRowSelection<ExplorerGetForStorageLocationItem, 'id'>(
            data,
            'id',
            onFiltersChange,
            filters.storageLocation,
        );

        return (
            <ExplorerDataTable
                ref={ref}
                data={data}
                columns={columns}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isFetching={isFetching}
                hasNextPage={hasNextPage}
                isLoading={isLoading}
                filters={filters}
                window={window}
                billingStatus={billingStatus}
                customDateRange={customDateRange}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                sorting={sorting}
                sortChangedInInterface={sortChangedInInterface}
            />
        );
    },
);
ExplorerViewStorageLocations.displayName = 'ExplorerViewStorageLocations';
